import { Platform } from 'react-native';
import { requireTracker, isTrackerReady, verifyTrackerAppId, getTrackerInfo } from './core';
import { isDebugSnowplowDisabled } from './debugState';

const DV_CONTENT_CONSUMPTION_SCHEMA = 'iglu:com.dgp/dv_content_consumption/jsonschema/1-0-0';
const DV_PRODUCT_SCHEMA = 'iglu:com.dgp/dv_product/jsonschema/1-0-3';

type DvProductContext = {
  service_category: string;
  content_id?: string | null;
  content_name: string | null;
  service_name?: string | null;
  service_type: string;
  campaign_id?: string | null;
};

const getTimestamp = () => new Date().toISOString();

const MAX_TRACKER_WAIT_RETRIES = 10;
const TRACKER_WAIT_INTERVAL_MS = 1000;

function waitForTracker(maxRetries: number): Promise<boolean> {
  return new Promise((resolve) => {
    let attempt = 0;
    const check = () => {
      if (isTrackerReady()) {
        resolve(true);
        return;
      }
      attempt++;
      if (attempt >= maxRetries) {
        resolve(false);
        return;
      }
      setTimeout(check, TRACKER_WAIT_INTERVAL_MS);
    };
    check();
  });
}

/**
 * Send a Snowplow DV_CONTENT_CONSUMPTION event using the React Native SDK
 * This method uses the official SDK's trackSelfDescribingEvent for comparison with HTTP method
 */
export function sendVideoStartEventViaSDK(productContext: DvProductContext): void {
  if (isDebugSnowplowDisabled()) {
    console.log(`[snowplow][DEBUG] sendVideoStartEventViaSDK blocked - content_id: ${productContext.content_id}`);
    return;
  }

  const scheduleTime = Date.now();
  console.log(`[${getTimestamp()}] [sdk-snowplow] 📤 SCHEDULING VIDEO START EVENT (SDK)`);
  console.log(`[${getTimestamp()}] [sdk-snowplow] Product context:`, JSON.stringify(productContext, null, 2));

  // Fire-and-forget: schedule in next tick
  const scheduler = Platform.OS === 'web' ? setTimeout : (typeof setImmediate !== 'undefined' ? setImmediate : setTimeout);

  scheduler(() => {
    const startTime = Date.now();
    console.log(`[${getTimestamp()}] [sdk-snowplow] Background task started ${startTime - scheduleTime}ms after schedule`);

    (async () => {
      try {
        if (!isTrackerReady()) {
          console.log(`[${getTimestamp()}] [sdk-snowplow] Tracker not ready, waiting up to ${MAX_TRACKER_WAIT_RETRIES}s...`);
          const ready = await waitForTracker(MAX_TRACKER_WAIT_RETRIES);
          if (!ready) {
            console.warn(`[${getTimestamp()}] [sdk-snowplow] ⚠️ Tracker not ready after ${MAX_TRACKER_WAIT_RETRIES}s, dropping event`);
            return;
          }
          console.log(`[${getTimestamp()}] [sdk-snowplow] ✅ Tracker became ready after wait`);
        }

        const trackerInfo = getTrackerInfo();
        verifyTrackerAppId(trackerInfo.countryCode);

        const tracker = requireTracker();

        // Validate that the tracker has the required method
        if (!tracker || typeof tracker.trackSelfDescribingEvent !== 'function') {
          const errorMsg = 'Tracker object does not have trackSelfDescribingEvent method';
          console.error(`[${getTimestamp()}] [sdk-snowplow] ❌ ${errorMsg}`);
          console.error(`[${getTimestamp()}] [sdk-snowplow] Tracker type:`, typeof tracker);
          console.error(`[${getTimestamp()}] [sdk-snowplow] Tracker keys:`, tracker ? Object.keys(tracker) : 'null');

          if (tracker) {
            const methods = Object.keys(tracker).filter(k => typeof tracker[k] === 'function');
            console.error(`[${getTimestamp()}] [sdk-snowplow] Available methods:`, methods);
          }

          return;
        }

        // Build the self-describing event
        const event = {
          schema: DV_CONTENT_CONSUMPTION_SCHEMA,
          data: {}
        };

        // Build the context entities
        const contextEntities = [
          {
            schema: DV_PRODUCT_SCHEMA,
            data: productContext
          }
        ];

        console.log(`[${getTimestamp()}] [sdk-snowplow] 📦 Sending via SDK - event: ${event.schema}, context: ${DV_PRODUCT_SCHEMA}`);

        // Track the self-describing event with context
        const trackResult = tracker.trackSelfDescribingEvent(event, contextEntities);

        // Handle both sync and async returns
        if (trackResult && typeof trackResult.then === 'function') {
          await trackResult;
        }

        const duration = Date.now() - startTime;
        console.log(`[${getTimestamp()}] [sdk-snowplow] ✅ SDK video_start sent in ${duration}ms | appId: ${trackerInfo.appId} | content_id: ${productContext.content_id}`);
      } catch (error) {
        const duration = Date.now() - startTime;
        console.error(`[${getTimestamp()}] [sdk-snowplow] ❌ Error sending event via SDK after ${duration}ms:`, error);
        console.error(`[${getTimestamp()}] [sdk-snowplow] Error details:`, {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        });
      }
    })();
  }, 0);

  console.log(`[${getTimestamp()}] [sdk-snowplow] Event scheduled successfully via SDK (returning immediately)`);
}

